<template>
  <div class="phone-number-list">
    <max-phone-number-warning :max-select-phone-numbers="maxSelectPhoneNumbers" v-if="!singleSelection && disabledCheckBoxList" />
    <div
      v-loading="loading"
      class="phone-number-list__content"
      :class="{ 'phone-number-list__content--empty': emptyResult }"
    >
      <p v-if="emptyResult">{{ emptyResultTips || $t('billing.pbx.no_results') }}</p>
      <!-- Single selection mode (Radio) for free trial -->
      <zm-radio-group
        v-if="!emptyResult && singleSelection"
        v-model="checkedSingleNumber"
        class="phone-number-list__radio-group"
      >
        <zm-radio
          v-for="item in currentList"
          :key="item.phoneNumber"
          :label="item.phoneNumber"
          :disabled="item.disabled"
          class="phone-number-list__radio-item"
        >
          {{ item.displayName }}
        </zm-radio>
      </zm-radio-group>
      <!-- Multiple selection mode (Checkbox) for regular flow -->
      <zm-checkbox-group v-if="!emptyResult && !singleSelection" v-model="checkedNumberList" :disabled="disabledCheckBoxList">
        <zm-checkbox v-for="item in currentList" :key="item.label" :label="item.label" :disabled="item.disabled">
          {{ item.displayName }}
        </zm-checkbox>
      </zm-checkbox-group>
    </div>
    <zm-button
      v-if="refreshBtnVisible"
      data-testid="checkout-button-194"
      class="phone-number-list__refresh"
      type="text"
      :disabled="disabledRefreshBtn"
      :icon="refreshIcon"
      @click="onRefresh"
    >
      {{ $t('billing.pbx.refresh') }}
    </zm-button>
  </div>
</template>

<script>
import { RefreshSmall } from '@zoom/icons-vue';
import { isEqual, reduce, slice } from 'lodash';
import MaxPhoneNumberWarning from '@/plans/ZOOM_PHONE/component/max-phone-number-warning';
import computedMixin from '@/plans/ZOOM_PHONE/mixin/computed';
import { maxSelectPhoneNumbers, phoneNumberTypeMap } from '@/plans/ZOOM_PHONE/helper/constants';
import { logger } from '@/utils/log';
import { getCurrentTrackingPlanName } from '@/plans/ZOOM_PHONE/helper/utils';

const defaultPageCount = 10;

export default {
  name: 'PhoneNumberFormList',
  components: { MaxPhoneNumberWarning },
  mixins: [computedMixin],
  setup() {
    return {
      refreshIcon: RefreshSmall
    };
  },
  props: {
    loading: Boolean,
    type: {
      type: String,
      default: phoneNumberTypeMap.local,
    },
    phoneNumberList: {
      type: Array,
      default: () => [],
    },
    disabledRefresh: {
      type: Boolean,
      default: false,
    },
    checkedPhoneNumberMap: {
      type: Object,
      default: () => ({}),
    },
    localNumberShowClear: {
      type: Boolean,
    },
    portNumberCount: {
      type: Number,
      default: 0,
    },
    maxSelectPhoneNumbers: {
      type: Number,
      default: maxSelectPhoneNumbers,
    },
    emptyResultTips: {
      type: String,
      default: ''
    },
    singleSelection: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      checkedNumberList: [],
      checkedSingleNumber: '', // For single selection (radio)
      currentList: [],
      startCount: 0,
    };
  },
  computed: {
    trackingPlanName() {
      return getCurrentTrackingPlanName();
    },
    phoneNumberMap() {
      return reduce(
        this.phoneNumberList,
        (pre, item) => {
          return { ...pre, [item.label]: item };
        },
        {},
      );
    },
    checkedPhoneNumberMapKeys() {
      return Object.keys(this.checkedPhoneNumberMap);
    },
    disabledCheckBoxList() {
      return this.checkedPhoneNumberMapKeys?.length + this.portNumberCount >= this.maxSelectPhoneNumbers;
    },
    disabledRefreshBtn() {
      return this.disabledRefresh || this.loading;
    },
    emptyResult() {
      return this.currentList?.length === 0;
    },
    refreshBtnVisible() {
      return this.phoneNumberList?.length > 0;
    },
  },
  methods: {
    changeCurrentList(list = []) {
      this.currentList = this.mapDisabledPhoneNumberList(list);
    },
    gotoNextPage() {
      let prevStartCount = this.startCount; // 100

      if (this.startCount >= this.phoneNumberList.length) {
        prevStartCount = 0;
        this.startCount = prevStartCount + defaultPageCount;
      } else {
        this.startCount = this.startCount + defaultPageCount;
      }

      const currentList = slice(this.phoneNumberList, prevStartCount, this.startCount);

      this.changeCurrentList(currentList);
    },
    mapDisabledPhoneNumberList(list) {
      if (this.type === phoneNumberTypeMap.local) {
        return list.map((item) => {
          const isCurrent = !!this.checkedPhoneNumberMap[item.phoneNumber];
          // For single selection (radio), no need to disable
          const disabled = this.singleSelection ? false : (this.localNumberShowClear ? false : isCurrent);

          return {
            ...item,
            disabled,
          };
        });
      }

      return list;
    },
    onRefresh() {
      this.gotoNextPage();
      this.$tracking.buyInteraction(
        `${this.trackingPlanName} numbers`,
        `${this.callingPlanName} - pick your numbers - ${this.type} - refresh results`,
      );
    },
    checkExistUnreadPhoneNumbers() {
      return this.phoneNumberList?.length > 0;
    },
  },
  watch: {
    phoneNumberList: {
      deep: true,
      immediate: true,
      handler() {
        this.startCount = 0;
        this.gotoNextPage();
      },
    },
    checkedNumberList: {
      handler(list) {
        if (this.singleSelection) return; // Skip for single selection mode

        // Find the dto with the current number complete according to the number id currently selected by the user
        const checkedMap = reduce(
          list,
          (pre, p) => {
            // search by current phone number list or previously selected number list
            const item = this.phoneNumberMap[p] || this.checkedPhoneNumberMap[p];
            return {
              ...pre,
              ...(item ? { [p]: item } : {}),
            };
          },
          {},
        );

        this.$emit('checkedNumberList', checkedMap);
      },
    },
    checkedSingleNumber: {
      handler(phoneNumber) {
        if (!this.singleSelection) return; // Skip for multiple selection mode

        // For single selection, emit only the selected number
        const item = this.phoneNumberMap[phoneNumber] || this.checkedPhoneNumberMap[phoneNumber];
        const checkedMap = item ? { [phoneNumber]: item } : {};

        this.$emit('checkedNumberList', checkedMap);
      },
    },
    checkedPhoneNumberMapKeys: {
      immediate: true,
      handler(val) {
        // When a customer deletes a previously selected number, should trigger checklist change
        if (this.singleSelection) {
          // For single selection, set the first (and only) value
          this.checkedSingleNumber = val[0] || '';
        } else {
          // For multiple selection
          if (!isEqual(val, this.checkedNumberList)) {
            this.checkedNumberList = val;
          }
        }
      },
    },
    localNumberShowClear: {
      immediate: true,
      handler(val) {
        logger.log('localNumberShowClear', val);

        if (this.type === phoneNumberTypeMap.local) {
          this.changeCurrentList(this.currentList);
        }
      },
    },
    emptyResult: {
      handler(emptyResult) {
        if (emptyResult) {
          this.$tracking.buyInteraction(
            `${this.trackingPlanName} numbers`,
            `${this.callingPlanName} - pick your numbers - ${this.type} - error - no results found please try again`,
          );
        }
      },
    },
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.phone-number-list {
  margin-top: 8px;
  text-align: center;

  .zbo-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    row-gap: 8px;
    .zbo-checkbox {
      width: 50%;
      margin: 0;
      &__label {
        padding: 0;
      }
    }
  }

  .max-phone-number {
    &__alert {
      margin-bottom: 8px;
    }
  }

  &__radio-group {
    width: 100%;
    text-align: left;
    display: flex;
    flex-wrap: wrap;
  }

  &__radio-item {
    width: 50%;
    margin: 0;
    margin-bottom: 12px;

    &.zbo-radio {
      margin-right: 0;
    }
  }

  &__content {
    width: 100%;
    padding: 16px 15px;
    min-height: 210px;
    border: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
    border-radius: 10px;
    margin: auto;

    &--empty {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .zbo-loading-mask {
      border-radius: 15px;

      .zbo-loading-spinner {
        svg {
          width: 24px;
        }
      }
    }
    .zbo-checkbox__wrap {
      font-size: 16px;
      line-height: 24px;
      padding: 4px 0;
    }
    .zbo-checkbox__inner {
      margin-top: 2px;
    }
  }

  &__refresh {
    text-transform: capitalize;
    margin-top: 6px;
  }
}
</style>
